/**
 * 组装 Temu bg.goods.add / bg.glo.goods.add 业务参数（不含 type/sign）。
 * 对齐官方「货品发布流程」字段约定。
 */

import {
  buildProductSkuAccessoriesReq,
  buildProductSkuMultiPackReq,
  buildProductSkuSensitiveAttrReq,
  buildProductSkuSuggestedPriceReq,
} from '@/temu-platform/utils/temuSkuPackage';
import {
  buildTemuProductOuterPackageImageReqs,
  buildTemuProductOuterPackageReq,
} from '@/temu-platform/utils/temuOuterPackage';
import { normalizeTemuTryOnResult } from '@/temu-platform/utils/temuOfficialSizeChart';
import { isStrictTemuImageCdnUrl } from '@/pages/ipe/workbench/utils/convertListingImageToTemu';
import { buildTemuProductOrigin } from '@/temu-platform/utils/temuProductOrigin';
import { buildTemuGoodsVehiclePropertyRelation } from '@/temu-platform/utils/temuVehicleLibraryRequired';
import {
  buildTemuCustomizedTechnologyReq,
  buildTemuManufacturingLocation,
  isTemuCustomProductEnabled,
} from '@/temu-platform/utils/temuCustomizedTechnology';

const CAT_KEYS = [
  'cat1Id', 'cat2Id', 'cat3Id', 'cat4Id', 'cat5Id',
  'cat6Id', 'cat7Id', 'cat8Id', 'cat9Id', 'cat10Id',
];

function toInt(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
}

/** 站内录入为元/美金（主单位）→ 平台要分/美分（×100）。 */
function toMoneyCents(majorUnitAmount) {
  const n = Number(majorUnitAmount);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

/** 解析 SKU/表单上的申报价主单位（元或美金），勿把已换算的分/美分再当主单位。 */
function resolveSkuSupplierPriceMajor(sku = {}, form = {}) {
  const candidates = [
    sku.supplierPriceYuan,
    sku.supplyPrice,
    sku.price,
    sku.supplierPrice,
    form.defaultPriceYuan,
    form.supplyPrice,
  ];
  for (const c of candidates) {
    if (c == null || String(c).trim() === '') continue;
    const n = Number(c);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

/** 体积 mm：优先 lengthMm；否则把页面 cm 换算为 mm */
function resolveSkuLengthMm(sku) {
  if (sku.lengthMm != null && String(sku.lengthMm).trim() !== '') {
    return toInt(sku.lengthMm, 100);
  }
  if (sku.len != null && String(sku.len).trim() !== '') {
    return toInt(sku.len, 100);
  }
  const cm = Number(sku.inputLen || sku.length);
  if (Number.isFinite(cm) && cm > 0) return Math.trunc(cm * 10);
  return 100;
}

function resolveSkuWidthMm(sku) {
  if (sku.widthMm != null && String(sku.widthMm).trim() !== '') {
    return toInt(sku.widthMm, 100);
  }
  const cm = Number(sku.inputWidth || sku.width);
  if (Number.isFinite(cm) && cm > 0) return Math.trunc(cm * 10);
  return 100;
}

function resolveSkuHeightMm(sku) {
  if (sku.heightMm != null && String(sku.heightMm).trim() !== '') {
    return toInt(sku.heightMm, 50);
  }
  const cm = Number(sku.inputHeight || sku.height);
  if (Number.isFinite(cm) && cm > 0) return Math.trunc(cm * 10);
  return 50;
}

/** 重量 mg：优先 weightMg；否则把页面 g 换算为 mg（勿把克数直接当 mg） */
function resolveSkuWeightMg(sku) {
  if (sku.weightMg != null && String(sku.weightMg).trim() !== '') {
    return toInt(sku.weightMg, 100000);
  }
  const grams = Number(sku.inputWeight || sku.weightGrams || sku.weightUi || sku.weight);
  if (Number.isFinite(grams) && grams > 0) return Math.trunc(grams * 1000);
  return 100000;
}

/** 货品净重 mg：页面按 g 录入 → productSkuNetWeightReq；勿读净含量 netContent */
function resolveSkuNetWeightMg(sku) {
  if (sku.netWeightMg != null && String(sku.netWeightMg).trim() !== '') {
    return toInt(sku.netWeightMg, 0);
  }
  const grams = Number(sku.netWeight || sku.netWeightGrams || '');
  if (Number.isFinite(grams) && grams > 0) return Math.trunc(grams * 1000);
  return 0;
}

/**
 * 包装扩展属性（对齐官方文档 + bg.glo.goods.add 样例）：
 * - 体积必填 len/width/height，单位 mm（页面 cm ×10）
 * - 重量必填 value，单位 mg（页面 g ×1000）
 * - 敏感属性必填：挂在 WhExtAttr 内 { isSensitive, sensitiveList }
 * - 净重部分类目必填：productSkuNetWeightReq.value 单位 mg（有值才下发）
 */
function buildProductSkuWhExtAttrReq(sku = {}, form = {}) {
  const sensitive = sku.productSkuSensitiveAttrReq
    || buildProductSkuSensitiveAttrReq(sku, form);
  const req = {
    productSkuVolumeReq: {
      len: resolveSkuLengthMm(sku),
      width: resolveSkuWidthMm(sku),
      height: resolveSkuHeightMm(sku),
    },
    productSkuWeightReq: {
      value: resolveSkuWeightMg(sku),
    },
    productSkuBarCodeReqs: Array.isArray(sku.productSkuBarCodeReqs)
      ? sku.productSkuBarCodeReqs
      : [],
    // GLO 样例：敏感属性在 WhExtAttr 内；缺省会报「请填写sku敏感属性」
    productSkuSensitiveAttrReq: {
      isSensitive: Number(sensitive.isSensitive) === 1 ? 1 : 0,
      sensitiveList: Array.isArray(sensitive.sensitiveList) ? sensitive.sensitiveList : [],
    },
    productSkuSensitiveLimitReq: sku.productSkuSensitiveLimitReq || {},
  };

  const netMg = resolveSkuNetWeightMg(sku);
  if (netMg > 0) {
    req.productSkuNetWeightReq = { value: netMg };
  }

  return req;
}

/**
 * @param {object} form
 * @param {{ semiManaged?: boolean, partialEdit?: boolean }} opts
 *   partialEdit=true：编辑已有货品（如复色），只提交变更的规格与图片；
 *   未变更的标题/素材图/外包装沿用平台原值，不做必填校验也不下发。
 */
export function buildTemuGoodsAddPayload(form, opts = {}) {
  const semiManaged = Boolean(opts.semiManaged);
  const partialEdit = Boolean(opts.partialEdit);
  const catIds = Array.isArray(form.catIds) ? form.catIds.map((id) => toInt(id, 0)).filter((id) => id > 0) : [];
  const cats = {};
  for (let i = 0; i < CAT_KEYS.length; i += 1) {
    cats[CAT_KEYS[i]] = catIds[i] || 0;
  }

  const productPropertyReqs = (Array.isArray(form.productPropertyReqs) ? form.productPropertyReqs : [])
    .filter(Boolean)
    .map((p) => {
      const controlType = p.controlType != null ? toInt(p.controlType) : undefined;
      const isComposition = controlType === 16
        || (p.numberInputValue != null && String(p.numberInputValue).trim() !== '');
      let valueUnit = String(p.valueUnit || '').trim();
      if (!valueUnit && isComposition) valueUnit = '%';
      return {
        templatePid: toInt(p.templatePid),
        pid: toInt(p.pid),
        refPid: toInt(p.refPid),
        propName: String(p.propName || ''),
        vid: toInt(p.vid),
        propValue: String(p.propValue || ''),
        valueUnit,
        numberInputValue: p.numberInputValue != null ? String(p.numberInputValue) : '',
        ...(controlType != null ? { controlType } : {}),
        ...(p.valueExtendInfo != null ? { valueExtendInfo: String(p.valueExtendInfo) } : {}),
      };
    });

  const productSpecPropertyReqs = (Array.isArray(form.productSpecPropertyReqs) ? form.productSpecPropertyReqs : [])
    .filter(Boolean)
    .map((p) => ({
      templatePid: toInt(p.templatePid),
      pid: toInt(p.pid),
      refPid: toInt(p.refPid),
      propName: String(p.propName || ''),
      vid: toInt(p.vid, 0),
      propValue: String(p.propValue || ''),
      valueUnit: String(p.valueUnit || ''),
      specId: toInt(p.specId, 0),
      parentSpecId: toInt(p.parentSpecId, 0),
      valueGroupId: toInt(p.valueGroupId, 0),
      valueGroupName: String(p.valueGroupName || ''),
      ...(p.numberInputValue != null ? { numberInputValue: String(p.numberInputValue) } : {}),
    }));

  const carouselImageUrls = (Array.isArray(form.carouselImageUrls) ? form.carouselImageUrls : [])
    .map((u) => String(u || '').trim())
    .filter(Boolean);

  const productSkcReqs = (Array.isArray(form.productSkcReqs) ? form.productSkcReqs : []).map((skc) => {
    const skuList = (Array.isArray(skc.productSkuReqs) ? skc.productSkuReqs : []).map((sku) => {
      const productSkuId = toInt(sku.productSkuId, 0);
      const base = {
        ...(productSkuId > 0 ? { productSkuId } : {}),
        extCode: String(sku.extCode || ''),
        currencyType: String(sku.currencyType || form.currency || 'USD'),
        thumbUrl: String(sku.thumbUrl || ''),
        productSkuSpecReqs: Array.isArray(sku.productSkuSpecReqs) ? sku.productSkuSpecReqs : [],
        productSkuWhExtAttrReq: buildProductSkuWhExtAttrReq(sku, form),
        productSkuMultiPackReq: sku.productSkuMultiPackReq || buildProductSkuMultiPackReq(sku),
      };
      const suggested = sku.productSkuSuggestedPriceReq
        || buildProductSkuSuggestedPriceReq(sku, form.currency || sku.currencyType || 'USD');
      if (suggested) {
        base.productSkuSuggestedPriceReq = suggested;
      }
      const accessories = sku.productSkuAccessoriesReq || buildProductSkuAccessoriesReq(sku);
      if (accessories) {
        base.productSkuAccessoriesReq = accessories;
      }
      // 申报价：页面按元/美金填；平台 supplierPrice / siteSupplierPrices.supplierPrice 要分/美分
      const priceMajor = resolveSkuSupplierPriceMajor(sku, form);
      if (semiManaged) {
        let siteRows;
        if (Array.isArray(sku.siteSupplierPrices) && sku.siteSupplierPrices.length) {
          siteRows = sku.siteSupplierPrices;
        } else if (Array.isArray(form.siteIds) && form.siteIds.length) {
          siteRows = form.siteIds.map((sid) => ({ siteId: sid }));
        } else if (form.siteId) {
          siteRows = [{ siteId: form.siteId }];
        } else {
          siteRows = [];
        }
        base.siteSupplierPrices = siteRows.map((sp) => ({
          siteId: toInt(sp.siteId),
          supplierPrice: toMoneyCents(
            sp.supplierPriceYuan ?? sp.priceYuan ?? sp.supplierPrice ?? priceMajor,
          ),
        }));
      } else {
        base.supplierPrice = toMoneyCents(priceMajor);
      }
      // 全托管/本土跳过站点·仓·运费：不传仓库库存；仅半托管带仓库存
      if (semiManaged) {
        if (sku.productSkuStockQuantityReq) {
          base.productSkuStockQuantityReq = sku.productSkuStockQuantityReq;
        } else if (form.warehouseId) {
          base.productSkuStockQuantityReq = {
            warehouseStockQuantityReqs: [{
              warehouseId: String(form.warehouseId),
              targetStockAvailable: toInt(sku.stock ?? form.defaultStock ?? 0),
            }],
          };
        }
      }
      base.productSkuSensitiveAttrReq = sku.productSkuSensitiveAttrReq
        || buildProductSkuSensitiveAttrReq(sku, form);
      // 同步一份到 WhExtAttr（官方 GLO 样例挂在此处）
      if (base.productSkuWhExtAttrReq && !base.productSkuWhExtAttrReq.productSkuSensitiveAttrReq) {
        base.productSkuWhExtAttrReq.productSkuSensitiveAttrReq = base.productSkuSensitiveAttrReq;
      }
      return base;
    });

    const mainSpecs = Array.isArray(skc.mainProductSkuSpecReqs) && skc.mainProductSkuSpecReqs.length
      ? skc.mainProductSkuSpecReqs
      : (form.clothingCategory
        ? []
        : [{ parentSpecId: 0, parentSpecName: '', specId: 0, specName: '' }]);

    const productSkcId = toInt(skc.productSkcId, 0);
    return {
      ...(productSkcId > 0 ? { productSkcId } : {}),
      extCode: String(skc.extCode || ''),
      previewImgUrls: (Array.isArray(skc.previewImgUrls) ? skc.previewImgUrls : carouselImageUrls.slice(0, 1))
        .map((u) => String(u || '').trim())
        .filter(Boolean),
      mainProductSkuSpecReqs: mainSpecs,
      productSkuReqs: skuList,
    };
  });

  // 官方样例：productWhExtAttrReq.productOrigin（勿顶层传 productOrigin）
  // CN 时必须带官方长枚举 region2Id，否则平台报「省份为必填项」
  const productOrigin = buildTemuProductOrigin(form);

  const payload = {
    ...cats,
    productName: String(form.productName || '').trim(),
    productPropertyReqs,
    productSpecPropertyReqs,
    productSkcReqs,
    productWhExtAttrReq: {
      outerGoodsUrl: String(form.outerGoodsUrl || ''),
      productOrigin,
    },
    sizeTemplateIds: Array.isArray(form.sizeTemplateIds) ? form.sizeTemplateIds : [],
    showSizeTemplateIds: Array.isArray(form.showSizeTemplateIds) ? form.showSizeTemplateIds : [],
  };

  const goodsModelReqs = (Array.isArray(form.goodsModelReqs)
    ? form.goodsModelReqs
    : (Array.isArray(form.goodsModels) ? form.goodsModels : []))
    .filter((m) => m && typeof m === 'object')
    .map((m) => {
      const {
        _templateId,
        _templateName,
        _portraitIpe,
        templateId: _ignoredTemplateId,
        sizeValueId: _sizeValueId,
        ...rest
      } = m;
      const profile = String(rest.modelProfileUrl || '').trim();
      if (profile && !isStrictTemuImageCdnUrl(profile)) {
        delete rest.modelProfileUrl;
      }
      return rest;
    })
    .filter((m) => String(m.modelName || '').trim())
    .map((m) => {
      const sizeName = String(m.sizeSpecName || '').trim();
      const sizeSpecId = toInt(m.sizeSpecId, 0);
      // 丢弃非法试穿尺码，避免平台 1000003「模特试穿尺码不合法：均码」
      if (!sizeName || /均码|free\s*size|one\s*size/i.test(sizeName) || !(sizeSpecId > 0)) {
        return null;
      }
      return {
        ...m,
        sizeSpecId,
        sizeSpecName: sizeName,
        tryOnResult: normalizeTemuTryOnResult(m.tryOnResult),
      };
    })
    .filter(Boolean);
  if (goodsModelReqs.length) {
    payload.goodsModelReqs = goodsModelReqs;
  } else if (!partialEdit && (Array.isArray(form.goodsModels) ? form.goodsModels : []).length) {
    throw new Error('模特试穿尺码无效，请从商品已选尺码中重新选择');
  }

  const materialImgUrl = String(form.materialImgUrl || '').trim();
  if (materialImgUrl) {
    payload.materialImgUrl = materialImgUrl;
  } else if (!partialEdit) {
    throw new Error('请上传素材图（1:1，边长≥800px）');
  }

  if (carouselImageUrls.length && !form.clothingCategory) {
    payload.carouselImageUrls = carouselImageUrls;
  }

  if (Array.isArray(form.productI18nReqs) && form.productI18nReqs.length) {
    payload.productI18nReqs = form.productI18nReqs;
  }

  const videos = (Array.isArray(form.productCarouseVideoReqList) ? form.productCarouseVideoReqList : [])
    // 平台 bg.glo.goods.add 要求主图视频必带 vid；仅本地/预览地址不算数，避免 "Video VID cannot be empty"
    .filter((v) => v && String(v.vid || '').trim())
    .map((v) => ({
      vid: String(v.vid || ''),
      coverUrl: String(v.coverUrl || ''),
      videoUrl: String(v.videoUrl || ''),
      width: toInt(v.width, 0),
      height: toInt(v.height, 0),
    }));
  if (videos.length) {
    payload.productCarouseVideoReqList = videos;
  }

  const guideFiles = (Array.isArray(form.guideFiles) ? form.guideFiles : [])
    .map((f) => ({
      fileName: String(f.fileName || '').trim(),
      pdfMaterialId: toInt(f.pdfMaterialId || f.pdfId, 0),
      languages: (Array.isArray(f.languages) ? f.languages : []).map(String).filter(Boolean),
    }))
    .filter((f) => f.pdfMaterialId > 0 && f.fileName);
  if (guideFiles.length) {
    payload.productGuideFileReqs = guideFiles.map((f) => ({
      fileName: f.fileName,
      pdfMaterialId: f.pdfMaterialId,
      languages: f.languages.length ? f.languages : ['en'],
    }));
  }

  const originCerts = (Array.isArray(form.originCertFiles) ? form.originCertFiles : [])
    .map((f) => ({
      fileName: String(f.fileName || '').trim(),
      fileUrl: String(f.fileUrl || f.url || '').trim(),
    }))
    .filter((f) => f.fileName && f.fileUrl)
    .slice(0, 1);
  if (originCerts.length) {
    if (!payload.productWhExtAttrReq) payload.productWhExtAttrReq = { outerGoodsUrl: '' };
    payload.productWhExtAttrReq.productOriginCertFiles = originCerts;
  }

  // 官方服饰样例：shape+type 与外包装图成套；缺了会报 Outer packaging information is incomplete。
  if (partialEdit) {
    // 编辑（复色）沿用当前 SPU 的外包装：本地有完整值就随包带上，没有就交给平台保留原值，
    // 不做必填校验，避免让用户为新颜色重复上传外包装形状/图片。
    try {
      const outerPkg = buildTemuProductOuterPackageReq(form, { required: false });
      const outerPkgImages = buildTemuProductOuterPackageImageReqs(form);
      if (outerPkg && outerPkgImages.length) {
        payload.productOuterPackageReq = outerPkg;
        payload.productOuterPackageImageReqs = outerPkgImages;
      }
    } catch {
      // 编辑时外包装不参与校验
    }
  } else {
    const outerPkg = buildTemuProductOuterPackageReq(form, { required: true });
    payload.productOuterPackageReq = outerPkg;
    const outerPkgImages = buildTemuProductOuterPackageImageReqs(form);
    if (!outerPkgImages.length) {
      throw new Error('请上传货品外包装图片（至少 1 张，须为 Temu 图址）');
    }
    payload.productOuterPackageImageReqs = outerPkgImages;
  }

  // 半托管才传运费模板 / 站点仓路由 / 绑定站点；全托管与本土跳过本步骤
  if (semiManaged) {
    if (form.freightTemplateId) {
      payload.productShipmentReq = {
        freightTemplateId: String(form.freightTemplateId),
        shipmentLimitSecond: toInt(form.shipmentLimitSecond || 86400 * 2),
      };
    }
    if (form.warehouseId && Array.isArray(form.siteIds) && form.siteIds.length) {
      payload.productWarehouseRouteReq = {
        targetRouteList: [{
          warehouseId: String(form.warehouseId),
          siteIdList: form.siteIds.map((id) => toInt(id)).filter((id) => id > 0),
        }],
      };
    }
    const bindSiteIds = Array.isArray(form.siteIds)
      ? form.siteIds.map((id) => toInt(id)).filter((id) => id > 0)
      : [];
    if (form.productSemiManagedReq) {
      payload.productSemiManagedReq = form.productSemiManagedReq;
    } else if (bindSiteIds.length) {
      payload.productSemiManagedReq = { bindSiteIds };
    }
  }

  const customProduct = isTemuCustomProductEnabled(form);
  payload.personalizationSwitch = customProduct ? 1 : 0;
  const tech = customProduct ? buildTemuCustomizedTechnologyReq(form) : null;
  const manufacturingLocation = buildTemuManufacturingLocation(form);
  // 全托管制造地点必填；半托管选填。普通品不能把制造地点塞进 customizedTechnologyReq，
  // 否则平台会当成定制品，并要求工艺类型、一级工艺。
  if (!semiManaged && !manufacturingLocation && !partialEdit) {
    throw new Error('全托管发品请选择制造地点');
  }
  const customizedTechnologyReq = tech
    ? (manufacturingLocation && !tech.manufacturingLocation
      ? { ...tech, manufacturingLocation }
      : tech)
    : null;
  const saleExt = {};
  if (customizedTechnologyReq) {
    saleExt.customizedTechnologyReq = customizedTechnologyReq;
  } else if (manufacturingLocation) {
    saleExt.manufacturingLocation = manufacturingLocation;
  }
  if (Object.keys(saleExt).length) {
    payload.productSaleExtAttrReq = saleExt;
  }

  const vehicleRel = buildTemuGoodsVehiclePropertyRelation(form);
  if (vehicleRel) {
    payload.goodsVehiclePropertyRelation = vehicleRel;
  }

  return payload;
}

/** 从类目路径节点列表提取 cat1…catN id */
export function catIdsFromPath(pathNodes) {
  return (Array.isArray(pathNodes) ? pathNodes : [])
    .map((n) => toInt(n?.catId ?? n?.id ?? n, 0))
    .filter((id) => id > 0);
}

/** 笛卡尔积生成 SKU 规格组合 */
export function cartesianSpecCombos(specGroups) {
  const groups = (Array.isArray(specGroups) ? specGroups : [])
    .filter((g) => Array.isArray(g.values) && g.values.length > 0);
  if (!groups.length) return [[]];
  return groups.reduce(
    (acc, group) => acc.flatMap((prefix) => group.values.map((v) => [
      ...prefix,
      {
        parentSpecId: toInt(group.parentSpecId),
        parentSpecName: String(group.parentSpecName || group.name || ''),
        specId: toInt(v.specId),
        specName: String(v.value || v.specName || ''),
        vid: toInt(v.vid),
        propName: String(group.name || ''),
        propValue: String(v.value || ''),
        templatePid: toInt(group.templatePid),
        pid: toInt(group.pid),
        refPid: toInt(group.refPid),
        valueUnit: String(group.valueUnit || ''),
        valueGroupId: toInt(v.group?.id ?? v.valueGroupId ?? 0),
        valueGroupName: String(v.group?.name ?? v.valueGroupName ?? ''),
      },
    ])),
    [[]],
  );
}

export function partitionAttrsTemplate(attrsResult) {
  const properties = Array.isArray(attrsResult?.properties)
    ? attrsResult.properties
    : (Array.isArray(attrsResult) ? attrsResult : []);
  const sale = [];
  const normal = [];
  for (const p of properties) {
    if (!p || typeof p !== 'object') continue;
    if (p.isSale) sale.push(p);
    else normal.push(p);
  }
  return {
    inputMaxSpecNum: toInt(attrsResult?.inputMaxSpecNum, 0),
    saleSpecs: sale,
    productAttrs: normal,
  };
}

export function normalizeCategoryList(result) {
  if (!result) return [];
  const list = result.categoryDTOList || result.categoryList || result.list;
  if (Array.isArray(list)) return list;
  if (Array.isArray(result)) return result;
  return [];
}
